"use client";

import { useId, useState } from "react";
import Image from "next/image";
import ChevronDownIcon from "@/components/core/icons/ChevronDownIcon";
import AlternativeSectionLabel from "./AlternativeSectionLabel";
import { GradientTextParser } from "@/components/shared/content/TextCombination";
import type { AlternativeSection } from "@/lib/content/schemas/alternatives";

export type AlternativeTableProps = Omit<Extract<AlternativeSection, { _type: "alternativeTable" }>, "_type">;

export default function AlternativeTable({ id, label, sectionNumber, heading, subtitle, dash0Logo, competitorLogo, rows, limitations }: AlternativeTableProps) {
  const [expanded, setExpanded] = useState(false);
  const rowsId = useId();
  const headingId = useId();
  const canExpand = rows.length > 1;

  return (
    <section id={id} className="container mx-auto scroll-mt-24 px-4 py-8 xl:px-[54px] xl:py-16">
      <div className="mx-auto mb-8 max-w-3xl text-center md:mb-12">
        <AlternativeSectionLabel label={label} number={sectionNumber} />
        <h2 id={headingId} className="m-0 whitespace-pre-line text-[32px] font-normal leading-[130%] text-white md:text-[36px]">
          <GradientTextParser text={heading} />
        </h2>
        <p className="mb-0 mt-6 whitespace-pre-line text-base leading-relaxed text-grey-400">{subtitle}</p>
      </div>
      <div className="relative">
        <div
          className="overflow-x-auto rounded-lg border border-grey-850 bg-grey-950 custom-scrollbar scrollbar-horizontal"
          role="region"
          aria-labelledby={headingId}
          tabIndex={0}
        >
          <div className="min-w-[760px] overflow-hidden">
            <table aria-labelledby={headingId} className="w-full min-w-[760px] table-fixed border-collapse text-left text-base leading-relaxed text-grey-400">
              <colgroup>
                <col className="w-[22%]" />
                <col className="w-[30%]" />
                <col className="w-[30%]" />
                <col className="w-[18%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-grey-850 divide-x divide-grey-850">
                  <td className="p-6" />
                  {[dash0Logo, competitorLogo].map((logo, index) => (
                    <th key={index} scope="col" className="p-6 align-middle font-normal">
                      <Image src={logo.src} alt={logo.alt} width={logo.width ?? 120} height={logo.height ?? 32} className={`m-0 w-auto max-w-full object-contain object-left ${index === 0 ? "box-content h-6 px-[22px] py-1" : "h-8"}`} />
                    </th>
                  ))}
                  <th scope="col" className="p-6 text-center align-middle font-normal text-white">Advantage</th>
                </tr>
              </thead>
              <tbody id={rowsId}>
                {rows.map((row, index) => {
                  const isPreview = !expanded && canExpand && index === 1;
                  // CSS clips the preview before hydration, keeping the first row
                  // at its natural height without a measurement-driven layout shift.
                  const contentClassName = isPreview ? "max-h-10 overflow-hidden" : undefined;
                  const advantage = row.advantage ?? "Dash0";
                  const isDash0Advantage = advantage.toLowerCase() === "dash0";
                  const advantageLogo = isDash0Advantage
                    ? dash0Logo
                    : [competitorLogo.alt.toLowerCase(), "competitor"].includes(advantage.toLowerCase())
                      ? competitorLogo
                      : undefined;

                  return (
                    <tr
                      key={index}
                      aria-hidden={(!expanded && canExpand && index >= 1) || undefined}
                      className={`border-b border-grey-850 last:border-b-0 divide-x divide-grey-850 ${!expanded && index > 1 ? "hidden" : ""} ${isPreview ? "h-[88px]" : ""}`}
                    >
                      <th scope="row" className="whitespace-pre-line p-6 align-top font-normal text-white">
                        <div className={contentClassName}>{row.label}</div>
                      </th>
                      <td className="whitespace-pre-line p-6 align-top">
                        <div className={contentClassName}>{row.dash0Text}</div>
                      </td>
                      <td className="whitespace-pre-line p-6 align-top">
                        <div className={contentClassName}>{row.competitorText}</div>
                      </td>
                      <td className="p-6 text-center align-middle">
                        <div className={contentClassName}>
                          <span className={`inline-flex max-w-full items-center justify-center whitespace-nowrap rounded-sm p-2 font-mono text-[9px] font-semibold uppercase leading-none tracking-widest ${advantageLogo ? "" : "bg-grey-900 text-grey-400"}`}>
                            {advantageLogo ? (
                              <Image
                                src={advantageLogo.src}
                                alt={advantageLogo.alt}
                                width={advantageLogo.width ?? 120}
                                height={advantageLogo.height ?? 32}
                                className={`m-0 w-auto max-w-full object-contain ${isDash0Advantage ? "box-content h-[18px] px-4 py-[3px]" : "h-6"}`}
                              />
                            ) : advantage}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {canExpand && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 bottom-0 h-[88px] bg-gradient-to-b from-transparent to-grey-950 transition-opacity duration-500 ease-in-out motion-reduce:transition-none ${expanded ? "opacity-0" : "opacity-100"}`}
          />
        )}
      </div>
      {canExpand && (
        <div className="mt-6 text-center">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={rowsId}
            onClick={() => setExpanded((value) => !value)}
            className="inline-flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border border-[#FF6B52]/60 bg-[#FF6B52]/10 px-6 py-3 text-base font-medium text-white transition-colors hover:border-[#FF6B52] hover:bg-[#FF6B52]/20 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#FF6B52]"
          >
            {expanded ? "Read less" : "Expand the deep dive"}
            <span aria-hidden="true" className={`transition-transform duration-500 ease-in-out motion-reduce:transition-none ${expanded ? "rotate-180" : ""}`}>
              <ChevronDownIcon />
            </span>
          </button>
        </div>
      )}
      {limitations && (
        <div className="mt-8 md:mt-12">
          <h2 className="mx-auto mb-8 max-w-3xl whitespace-pre-line text-center text-base font-normal leading-relaxed text-grey-400">{limitations.subtitle}</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {limitations.cards.map((card, index) => (
              <article key={index} className="min-w-0 rounded-lg bg-grey-900 p-4 lg:p-6">
                <h3 className="m-0 text-base font-bold leading-relaxed text-white">{card.title}</h3>
                <p className="mb-0 mt-3 whitespace-pre-line text-base leading-relaxed text-grey-400">{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
